import { useCallback, useContext, useRef } from 'react'
import type { MarkViewProps } from '@tiptap/core'
import { ReactMarkViewContext } from '@tiptap/react'
import { useComplianceDocumentManifestContextOptional } from '@/features/compliance-studio/machinery-directive/context/ComplianceDocumentManifestContext'
import { DismissibleComplianceMarkShell } from '../dismissible-compliance-mark-shell'
import { removeMark } from '../remove-mark-from-mark-view'
import { INSUFFICIENT_INFORMATION_MARK } from './insufficient-information-mark'

const FALLBACK_INSUFFICIENT_INFORMATION_MESSAGE =
  'Insufficient information: No relevant content was found in the product sources for this section. Please add source documents or complete manually.'

/**
 * Mark view: ProseMirror keeps canonical `[INSUFFICIENT_INFORMATION]` in the document;
 * we hide that text and show the localized string (dismissible). Last child is PM contentDOM.
 */
export function InsufficientInformationMarkView(props: MarkViewProps) {
  const manifestContext = useComplianceDocumentManifestContextOptional()
  const message = manifestContext
    ? manifestContext.getInsufficientInformationMessage()
    : FALLBACK_INSUFFICIENT_INFORMATION_MESSAGE
  const dismissLabel = manifestContext
    ? manifestContext.getDismissAriaLabel()
    : 'Dismiss notice'
  const { markViewContentRef } = useContext(ReactMarkViewContext)
  const hostRef = useRef<HTMLSpanElement | null>(null)
  const setHostRef = useCallback(
    (element: HTMLSpanElement | null) => {
      hostRef.current = element
      markViewContentRef(element)
    },
    [markViewContentRef]
  )

  const handleDismiss = () => {
    const host = hostRef.current
    if (!host) {
      return
    }
    removeMark(INSUFFICIENT_INFORMATION_MARK, props, host)
  }

  return (
    <DismissibleComplianceMarkShell
      hostRef={setHostRef}
      dismissLabel={dismissLabel}
      onDismiss={handleDismiss}
      variant='yellow'
    >
      <span className='text-sm leading-relaxed text-blue-950 dark:text-blue-100'>
        {message}
      </span>
    </DismissibleComplianceMarkShell>
  )
}
